import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { BrandId } from 'src/app/interfaces/brand';
import { FirstPartyLargeCardItem } from 'src/app/interfaces/perk';
import { PagesConfig } from 'src/app/interfaces/pageConfig';

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private config: Config;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private meta: Meta
  ) {}

  set(config: Config) {
    this.config = config;

    this.updateHeadTags();
  }

  get(key: string) {
    return this.config ? this.config[key] : null;
  }

  getAll() {
    return this.config;
  }

  getTextFromKey(key: string) {
    return this.config.brandTexts[key];
  }

  getNumberFromKey(key: string): number {
    return parseFloat(this.getTextFromKey(key)) || 0;
  }

  getTextsFromKeys(keys: string[]): { [p: string]: string }[] {
    return keys.map((key) => ({ [key]: this.getTextFromKey(key) }));
  }

  getListContentFromKey(key: string): string[] {
    const rawData = this.getTextFromKey(key);
    if (!rawData || !Array.isArray(rawData)) {
      return [];
    }
    return rawData.map((item: any) => item.content);
  }

  getCardListFromKey(key: string): FirstPartyLargeCardItem[] {
    const rawData = this.getTextFromKey(key);
    if (!rawData || !Array.isArray(rawData)) {
      return [];
    }
    return rawData.map((item: FirstPartyLargeCardItem) => ({
      imgSrc: item.imgSrc,
      text: item.text 
    }));
  }

  getObjectListFromKey<T>(key: string): T[] {
  const rawData = this.getTextFromKey(key);
  if (!rawData || !Array.isArray(rawData)) {
    return [];
  }
  return rawData as T[];
}

  getFeatureFlag(key: string): boolean {
    return this.config.featureFlags[key];
  }

  getPage<PageName extends keyof PagesConfig>(pageName: PageName): PagesConfig[PageName] {
    return this.config.pages?.[pageName];
  }

  isPetlogBrand(): boolean {
    return this.get('brandId') === BrandId.Petlog;
  }

  private updateHeadTags(): void {
    if (!this.config) {
      return;
    }

    const appVersion = this.get('version');
    if (appVersion) {
      this.meta.updateTag(
        { name: 'version', content: appVersion as string },
        'id="app-version"'
      );
    }

    const faviconElement = this.document.getElementById('app-favicon') as HTMLLinkElement;
    if (faviconElement) {
      faviconElement.href = this.get('favicon');
    }
  }
}
